import React from 'react';

/**
 * EGGS Section Component
 * Based on EGGS Design System specifications
 * 
 * @param {string} variant - default | primary | secondary | dark | light
 * @param {string} spacing - none | sm | md | lg | xl
 * @param {boolean} fullHeight - Make section full viewport height
 * @param {React.ReactNode} children - Section content
 * @param {string} className - Additional CSS classes
 * @param {object} props - Additional section props
 */
export default function Section({
  variant = 'default',
  spacing = 'lg',
  fullHeight = false,
  children,
  className = '',
  ...props
}) {
  // Base classes for all sections
  const baseClasses = [
    'w-full',
    fullHeight ? 'min-h-screen' : ''
  ].join(' ');

  // Variant-specific styles
  const variantClasses = {
    default: [
      'bg-white',
      'text-eggs-dark'
    ].join(' '),
    
    primary: [
      'bg-eggs-yellow',
      'text-eggs-dark'
    ].join(' '),
    
    secondary: [
      'bg-gray-100',
      'text-eggs-dark'
    ].join(' '),
    
    dark: [
      'bg-eggs-dark',
      'text-white'
    ].join(' '),
    
    light: [
      'bg-gray-50',
      'text-eggs-dark'
    ].join(' ')
  };

  // Spacing classes (vertical padding)
  const spacingClasses = {
    none: '',
    sm: 'py-8',      // 32px
    md: 'py-12',     // 48px
    lg: 'py-16',     // 64px
    xl: 'py-24'      // 96px
  };

  return (
    <section
      className={`${baseClasses} ${variantClasses[variant]} ${spacingClasses[spacing]} ${className}`}
      {...props}
    >
      {children}
    </section>
  );
}

// Export convenience components for common section variants
export const PrimarySection = (props) => (
  <Section variant="primary" {...props} />
);

export const SecondarySection = (props) => (
  <Section variant="secondary" {...props} />
);

export const DarkSection = (props) => (
  <Section variant="dark" {...props} />
);

export const LightSection = (props) => (
  <Section variant="light" {...props} />
);

export const HeroSection = (props) => (
  <Section fullHeight spacing="xl" {...props} />
);